// ...imports (unchanged)
import React, { useState, useEffect } from 'react';
import './css/Explore.css';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { countries } from 'countries-list';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { enUS } from 'date-fns/locale';
import emailjs from '@emailjs/browser';
import { Toaster, toast } from 'react-hot-toast';
import { db, collection, addDoc } from '../firebase';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import locations from '../data/trips';
import ReCAPTCHA from 'react-google-recaptcha';
import { format } from 'date-fns';

function DestinationPage() {
  const { name: destination } = useParams();
  const place = locations.find(loc => loc.name === destination);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    setError,
    clearErrors
  } = useForm({ mode: 'onChange' });

  const [userLocation, setUserLocation] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [childrenCount, setChildrenCount] = useState(0);
  const [childrenAges, setChildrenAges] = useState([]);
  const [currency, setCurrency] = useState('KES');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
      key: 'selection'
    }
  ]);

  // --- Geolocation ---
  useEffect(() => {
    fetch('https://ipapi.co/json')
      .then(res => res.json())
      .then(data => setUserLocation(data))
      .catch(() => setUserLocation({ country_code: 'KE' }));
  }, []);

  // --- Validate children ages ---
  const validateChildrenAges = () => {
    const invalid = childrenAges.some(age => {
      const num = parseInt(age, 10);
      return isNaN(num) || num >= 18 || num <= 0;
    });
    if (invalid) {
      toast.error("All children must be below 18 years old.");
      return false;
    }
    return true;
  };

  // --- Form Submission ---
  const onSubmit = async (data) => {
    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA.");
      return;
    }

    if (!validateChildrenAges()) return;

    try {
      const doc = {
        ...data,
        phone: data.phone,
        childrenAges: childrenAges.join(', '),
        budget: `${data.budgetAmount} (${currency})`,
        tripId: place?.id,
        tripName: place?.name,
        submittedAt: new Date().toISOString(),
        detectedLocation: userLocation,
        checkInDate: format(dateRange[0].startDate.toISOString(), 'yyyy-MM-dd'),
        checkOutDate: format(dateRange[0].endDate.toISOString(), 'yyyy-MM-dd')
      };

      await toast.promise(
        emailjs.send('service_z5z38vy', 'template_l69rocw', doc, 'iOQXap89SbtigOxLR'),
        { loading: 'Sending...', success: 'Sent!', error: 'Send failed' }
      );

      await addDoc(collection(db, 'bookings'), doc);

      toast.success("Form submitted successfully!");
      reset();
      setChildrenCount(0);
      setChildrenAges([]);
      setRecaptchaToken(null);
      setPhoneNumber('');
    } catch (error) {
      console.error(error);
      toast.error("Submission failed. Try again later.");
    }
  };

  return (
    <>
    <SEO
            title="Get A Quote - Joujo Safaris"
            description="Book luxury safaris with Joujo Safaris. Explore Kenya, Tanzania, and Africa's Big Five adventures, cultural tours, and tailor-made safari packages for unforgettable journeys."
            url="https://joujosafaris.com/"
            image="https://www.joujosafaris.com/static/media/46.827b5711e4d95d33cc79.jpg"
            keywords="Kenya safari tours, luxury safaris Africa, Big Five adventures, Maasai Mara safaris, Serengeti tours, cultural safaris Kenya, Joujo Safaris"
          />
      <Navbar />
      <div className="about-main-title container" style={{ marginTop: "100px", marginBottom: "10px" }}>
        <h4 className="text-color">Explore</h4>
        <h5>Africa and Beyond</h5>
      </div>

      <div className='container'>
        <div style={{ marginTop: "70px" }} className='dp-container'>
          <div className='dp-header'>
            <h2>Get Quotation</h2>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className='dp-form-group'>
            <label>Full Name*</label>
            <input {...register('fullName', { required: "Please enter name as it appears on your ID", minLength: 3 })} />
            {errors.fullName && <span className='dp-error'>{errors.fullName.message}</span>}

            <label>Email*</label>
            <input type="email" {...register('email', { required: "Email is required" })} />
            {errors.email && <span className='dp-error'>{errors.email.message}</span>}

            <label>City*</label>
            <input {...register('city', { required: "City is required" })} />
            {errors.city && <span className='dp-error'>{errors.city.message}</span>}

            <label>Nationality*</label>
            <select {...register('nationality', { required: "Nationality is required" })}>
              <option value="">Select</option>
              {Object.entries(countries).map(([code, c]) => (
                <option key={code} value={c.name}>{c.name}</option>
              ))}
            </select>
            {errors.nationality && <span className='dp-error'>{errors.nationality.message}</span>}

            <label>Phone Number*</label>
            <PhoneInput
              country={userLocation?.country_code?.toLowerCase() || 'ke'}
              value={phoneNumber}
              onChange={(value) => {
                setPhoneNumber(value);
                setValue("phone", "+" + value);
              }}
            />
            <input
              type="hidden"
              {...register("phone", {
                required: "Phone number is required",
              })}
            />
            {errors.phone && <span className='dp-error'>{errors.phone.message}</span>}

            <label>Destination*</label>
            <input type='text' {...register('arrivalCity', { required: "This Field is required" })} />
            {errors.arrivalCity && <span className='dp-error'>{errors.arrivalCity.message}</span>}

            <label>Travel Dates (Check-in/Check-out)</label>
            <DateRange
              editableDateInputs={true}
              onChange={item => setDateRange([item.selection])}
              moveRangeOnFirstSelection={false}
              ranges={dateRange}
              locale={enUS}
            />

            <div className='dp-flex'>
              <div className='dp-a'>
                <label>Adults*</label>
                <input type="number" {...register('adults', { required: "At least 1 adult required", min: 1 })} />
                {errors.adults && <span className='dp-error'>{errors.adults.message}</span>}
              </div>

              <div className='dp-b'>
                <label>Children</label>
                <input
                  type="number"
                  {...register('children', { min: 0 })}
                  onChange={e => {
                    const val = parseInt(e.target.value) || 0;
                    setChildrenCount(val);
                    setChildrenAges(Array(val).fill(''));
                  }}
                />
              </div>
            </div>

            {childrenCount > 0 && (
              <div className='dp-children-ages'>
                <label>Children Ages*</label>
                {childrenAges.map((age, idx) => (
                  <div key={idx}>
                    <input
                      type="number"
                      placeholder={`Child ${idx + 1} Age`}
                      value={age}
                      onChange={e => {
                        const val = e.target.value;
                        const updatedAges = [...childrenAges];
                        updatedAges[idx] = val;
                        setChildrenAges(updatedAges);

                        const ageNum = parseInt(val);
                        if (!val || isNaN(ageNum) || ageNum <= 0 || ageNum >= 18) {
                          setError(`childAge_${idx}`, {
                            type: 'manual',
                            message: 'Age must be between 1 and 17',
                          });
                        } else {
                          clearErrors(`childAge_${idx}`);
                        }
                      }}
                      required
                    />
                    {errors[`childAge_${idx}`] && (
                      <span className="dp-error">
                        {errors[`childAge_${idx}`].message}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <label>Estimated Budget*</label>
            <div className='dp-budget-row'>
              <input
                type="number"
                {...register('budgetAmount', {
                  required: "Budget is required",
                  min: {
                    value: 2000,
                    message: "Minimum budget is 2000"
                  }
                })}
              />
              <select value={currency} onChange={e => setCurrency(e.target.value)}>
                <option>KES</option>
                <option>USD</option>
                <option>EUR</option>
              </select>
            </div>
            {errors.budgetAmount && <span className='dp-error'>{errors.budgetAmount.message}</span>}

            <label>Additional Message</label>
            <textarea {...register('message')} />

            <div className='dp-captcha'>
              <label>reCAPTCHA Verification*</label>
              <ReCAPTCHA
                sitekey="6LcaXrwrAAAAAGm7Q3lJbS2g18A-bZ9RvCF7yTo3"
                onChange={token => setRecaptchaToken(token)}
                onExpired={() => setRecaptchaToken(null)}
              />
            </div>

            <button type="submit" className='dp-submit-btn'>Submit</button>
          </form>
        </div>
      </div>

      <Toaster position="top-right" />
      <Footer />
    </>
  );
}

export default DestinationPage;
